import { useState } from "react";
import STCfinderSearchCard from "~/pages/StcFinderPage/components/StcFinderSearchCard";
import { getSTCfinderResults } from "~/pages/StcFinderPage/api/getStcFinderResults";
import {
  type STCauttoSuggestData,
  type STCfinderMountData,
  type TypeSTCfinderSingleResult,
} from "~/pages/StcFinderPage/types";
import STCfinderTabularView from "~/pages/StcFinderPage/components/StcFinderTabularView";
import Spinner from "~/common/components/Spinner/Spinner";
import { transformMountData } from "~/pages/StcFinderPage/transformers";
import PageTitle from "~/common/components/PageTitle/v1/PageTitle";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { useTranslation } from "react-i18next";
import { styled } from "~/common/mui";
import { Alert } from "~/common/mui/Alert/Alert";

interface Props {
  mountData: STCfinderMountData;
}
const STCfinder = (props: Props) => {
  const { t } = useTranslation();
  const { mountData } = props;
  const [resultsList, setResultsList] = useState<TypeSTCfinderSingleResult[]>([]);
  const [statesList, setStatesList] = useState<STCauttoSuggestData[]>([]);
  const [resultsLoading, setResultsLoading] = useState<boolean>();
  const [resultsError, setResultsError] = useState(false);

  const isDesktop = useIsMinDesktopSize();

  const handleSearch = (country?: string, state?: string, zipCode?: string) => {
    setResultsLoading(true);
    setResultsError(false);
    getSTCfinderResults(country, state, zipCode)
      .then((response) => {
        setResultsList(response.results);
        setStatesList(response.states);
      })
      .catch(() => {
        setResultsError(true);
        setResultsList([]);
        setStatesList([]);
      })
      .finally(() => {
        setResultsLoading(false);
      });
  };

  const resultAmount = (
    <p className="scha__stc-finder__results__title">
      {t("STCfinder.numberOfContacts", { number: resultsList.length })}
    </p>
  );

  return (
    <StcFinderRoot>
      <PageTitle title={t("STCfinder.title")} />
      <StcFinderSubtitle>{t("STCfinder.description")}</StcFinderSubtitle>
      {isDesktop && resultAmount}

      <div>
        <STCfinderSearchCard
          countries={mountData.countries}
          states={statesList}
          searchResults={(country, state, zipCode) => handleSearch(country, state, zipCode)}
          pageCode={mountData.pageCode}
        />
        {!isDesktop && resultAmount}
        {resultsError && <Alert severity="error">{t("generic.error.ajax")}</Alert>}
        {resultsLoading ? <Spinner /> : <STCfinderTabularView resultsList={resultsList} />}
      </div>
    </StcFinderRoot>
  );
};

export function createSTCfinderPage(data: string) {
  const parsedData = JSON.parse(data);
  return <STCfinder mountData={transformMountData(parsedData)} />;
}

const StcFinderRoot = styled("div")(({ theme }) => ({
  "& .scha__stc-finder__search-card": {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(4),
    "& > * + *": {
      marginTop: theme.spacing(1),
    },
    [theme.breakpoints.up("desktop-s")]: {
      flexDirection: "row",
      padding: theme.spacing(3.75),
      "& > *": {
        width: "calc((100% - 64px) / 3)",
      },
      "& > * + *": {
        marginTop: 0,
        marginLeft: theme.spacing(4),
      },
    },
  },
  "& .scha__stc-finder__results__title": {
    ...theme.typography.titleMedium,
    marginTop: 0,
    marginBottom: theme.spacing(3),
    [theme.breakpoints.up("desktop-s")]: {
      ...theme.typography.titleLarge,
    },
  },
  "& .scha__stc-finder__results__name-cell": {
    display: "flex",
    flexDirection: "column",
    "& > * + *": {
      marginTop: theme.spacing(2),
    },
    [theme.breakpoints.up("desktop-s")]: {
      "& > * + *": {
        marginTop: 0,
      },
      "& > *:first-child": {
        marginBottom: theme.spacing(1.25),
      },
    },
  },
}));

const StcFinderSubtitle = styled("p")(({ theme }) => ({
  ...theme.typography.titleMedium,
  marginTop: 0,
  marginBottom: theme.spacing(3),
  [theme.breakpoints.up("desktop-s")]: {
    marginBottom: theme.spacing(3.75),
  },
}));
